import { useEffect, useRef, useState } from "react";
import { useCustomNavigation } from "@/hooks/useCustomNavigation";
import { Settings } from "@mui/icons-material";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../Utils/firebase";
import { useLocation } from "react-router-dom";
import { format } from "date-fns";

import "./avatarSelect.scss";

import { Scan } from "../../types/interfaces";
import { CircularProgress } from "@mui/material";
import CancelModal from "@/Components/Modal/CancelModal";
import { useAuth } from "@/context/auth.context";
import {
  setAvatarSharingData,
  setDeeplinkFlag,
  clearAvatarSharingData,
  getAvatarSharingData,
  shareRecipientLabel,
} from "../../Utils/avatarSharingStorage";
import { logError } from "../../Utils/logger";
import { getClientIdParam } from "@/Utils/deepLinkParams";
import { acknowledgeScanSubmitted } from "@/Utils/scanSubmittedMarker";
import { useScansList } from "@/hooks/useScansList";

const isMalchusEnv = process.env.REACT_APP_IS_MALCHUS_ENV === "true";
const isNeemsEnv = process.env.REACT_APP_IS_NEEMS_ENV === "true";

function updateListItems(documents: any[]): Scan[] {
  const pipelineStatusColors: Record<string, string> = {
    PROCESSING: "rgba(242, 189, 53, 1)",
    FAILURE: "var(--sm-error, rgba(255, 98, 126, 1))",
    SUCCESS: "var(--sm-success, rgba(129, 192, 131, 1))",
    APPROVED: "var(--sm-text-muted, #8A8A98)",
  };

  const scans: Scan[] = [];

  documents.forEach((document) => {
    const {
      pipelineStatus,
      storagePath,
      timestamp,
      name,
      failureReason,
      id,
      userId,
      clientIds,
      failureMessage,
    } = document;

    if (!pipelineStatus || !storagePath || !timestamp) {
      return;
    }

    const timeInterval = new Date(timestamp * 1000);
    const formattedDate = format(timeInterval, "MMM d yyyy, hh:mma");

    let status: string;
    let color: string;

    switch (pipelineStatus) {
      case "PROCESSING":
        status = "Avatar processing";
        color = pipelineStatusColors["PROCESSING"];
        break;
      case "FAILURE":
        status = "Avatar failed";
        color = pipelineStatusColors["FAILURE"];
        break;
      case "UPLOADING":
        status = "Submitting Scans ...";
        color = pipelineStatusColors["APPROVED"];
        break;

      case "APPROVED":
        status = "Avatar ready";
        color = pipelineStatusColors["SUCCESS"];
        break;
      default:
        status = "Avatar processing";
        color = pipelineStatusColors["PROCESSING"];
    }

    const scan: Scan = {
      name: name || "No Name",
      date: formattedDate,
      status: status,
      color: color,
      id: id,
      userId: userId,
      clientIds,
      failureReason,
      failureMessage,
      originalTimestamp: timestamp * 1000,
    };

    scans.push(scan);
  });
  // Sort scans by original timestamp (most recent first)
  scans.sort((a, b) => b.originalTimestamp - a.originalTimestamp);

  return scans;
}

const isSharableScan = (scan: any) => {
  const isSharable =
    scan.status === "Avatar ready" || scan.status === "Avatar processing";
  return isSharable;
};

function AvatarSelect() {
  const { user } = useAuth();
  const location = useLocation();

  const { navigateTo } = useCustomNavigation();
  const [clientName, setClientName] = useState("");
  const [name, setName] = useState("");

  const [client, setClient] = useState("");
  const [session, setSession] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Where "Yes, cancel" navigates to. The footer Cancel button leaves it at
  // home; the Settings icon sets it to /settings so the user still reaches
  // where they were headed once the share is cancelled.
  const [pendingDestination, setPendingDestination] = useState("/");

  const handleOpen = (destination = "/") => {
    setPendingDestination(destination);
    setIsModalOpen(true);
  };
  const handleClose = () => setIsModalOpen(false);
  const handleCancel = () => {
    setIsModalOpen(false);
    // If this cancel abandons an active store share, return the user to the
    // store (redirectUrl) via the /share-cancelled screen — that screen resets
    // the widget session and clears the sharing data itself. This applies to
    // both the footer Cancel and the Settings ⚙️ path (pendingDestination is
    // ignored when a store redirect is available). No redirectUrl → fall back to
    // clearing here and navigating to the in-app destination.
    const { redirectUrl } = getAvatarSharingData();
    if (redirectUrl) {
      navigateTo("/share-cancelled");
      return;
    }
    clearAvatarSharingData();
    navigateTo(pendingDestination);
  };

  const [selectedScanId, setSelectedScanId] = useState("");
  // True once the user has picked a scan themselves. From then on an update to
  // some scan never moves their choice, unless it makes it unsendable.
  const userPicked = useRef(false);
  // From the app's react-query cache (useScansList). `docs` is undefined until
  // an answer exists: "not known yet", never "No recent avatars".
  const { docs, showSpinner, submissionInFlight } = useScansList("AvatarSelect");

  const scans = docs ? updateListItems(docs) : [];
  const canCreateNewScan = submissionInFlight === false;

  // Until the user picks, the selection follows the first scan that can be
  // sent. After they pick it stays theirs, unless an update makes it
  // unsendable. It used to be re-chosen on EVERY snapshot of the whole list,
  // so a write to any of the user's scans moved their choice.
  useEffect(() => {
    if (!docs) return;
    const listed = updateListItems(docs);
    const selected = listed.find((scan) => scan.id === selectedScanId);
    if (userPicked.current && selected && isSharableScan(selected)) return;
    userPicked.current = false;
    const first = listed.find(
      (scan) =>
        scan.status !== "Avatar failed" &&
        scan.status !== "Submitting Scans ..."
    );
    if (first) {
      setSelectedScanId(first.id);
      setName(first.name);
    }
  }, [docs, selectedScanId]);

  const validOrg = async (completeID: string) => {
    try {
      const querySnapshot = await getDocs(
        query(collection(db, "clientIds"), where("clientId", "==", completeID))
      );

      if (!querySnapshot.empty) {
        const documentData = querySnapshot.docs[0].data();
        const resolved =
          documentData.orgName?.trim() || documentData.name?.trim() || "";
        if (resolved) {
          setClientName(resolved);
          return;
        }
      }
    } catch (error) {
      // Surfaced to pwa_logs (not just the device console) so we have telemetry
      // on how often / why this one-shot query fails — Firestore `code` tells
      // permission-denied (auth-token race) from unavailable (network blip).
      logError("[AvatarSelect] clientIds org query failed", {
        code: (error as { code?: string })?.code ?? null,
        message: (error as Error)?.message ?? null,
        clientId: completeID,
      });
    }

    // Query empty or transiently failed — fall back to the org name App.tsx
    // resolved and persisted on this deep-link entry, but ONLY if it belongs to
    // the exact clientId we're resolving, so the banner can never imply a
    // previous client's org. This is display-only; it does not affect the share
    // recipient (that is always the deep-link clientId). Never clobber with "".
    const persisted = getAvatarSharingData();
    if (persisted.clientId === completeID && persisted.orgName) {
      setClientName(persisted.orgName);
    }
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hasVisited = sessionStorage.getItem("hasVisited");

    if (!hasVisited) {
      const timer = setTimeout(() => {
        setLoading(false);
        sessionStorage.setItem("hasVisited", "true");
      }, 3000);

      return () => clearTimeout(timer);
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    async function handleGettingSession() {
      if (!user) return;
      const urlParams = new URLSearchParams(location.search);
      // clientId (the share recipient) comes ONLY from the deep link
      // (`/send-avatar?clientId=…`, which the API always templates in). We do NOT
      // fall back to persisted storage here: a stale clientId from a previous
      // share would silently send this scan to the wrong client. If the deep
      // link somehow lacks it, fail safe — leave `client` empty (Confirm stays
      // disabled) rather than risk sharing with the wrong org.
      // Case-insensitive (see @/Utils/deepLinkParams): a link that lost its
      // scheme arrives with `clientid`. `sessionId` below stays strict.
      const clientId = getClientIdParam(location.search) || "";
      const sessionId = urlParams.get("sessionId");
      if (isMalchusEnv) {
        if (clientId !== "715053") return;
      }
      if (isNeemsEnv) {
        if (clientId !== "553777") return;
      }
      if (!clientId) return;

      // Set the clientId and resolve the org name UP FRONT, straight from the
      // deep link. `client` gates the Confirm button and `clientName` fills the
      // "Sending Avatar to" banner, so they must not sit behind the
      // widget_session read below: that read used to run first and, being
      // unguarded, a single transient getDoc failure (flaky network / Firestore
      // `unavailable`) rejected this whole function and skipped both — silently
      // blanking the banner and disabling sharing. validOrg is fire-and-forget
      // (it owns its own error handling) so it can't block this either.
      setClient(clientId);
      validOrg(clientId);

      if (!sessionId) return;
      setSession(sessionId);

      // Session read/update is best-effort: if it fails the user can still
      // share (client is already set). Never let it throw out of here.
      try {
        const sessionRef = doc(db, "widget_sessions", sessionId);
        const sessionDoc = await getDoc(sessionRef);
        if (sessionDoc.exists()) {
          const { activeScan } = sessionDoc.data();
          if (activeScan) {
            // Already shared on this session — go straight to the sent screen.
            // Use the persisted org only if it matches this clientId (never a
            // previous client's), else the live-resolved clientName.
            const persisted = getAvatarSharingData();
            const redirectOrg =
              persisted.clientId === clientId && persisted.orgName
                ? persisted.orgName
                : clientName;
            return navigateTo("/measurements-sent", {
              state: { orgName: redirectOrg },
            });
          }
          await updateDoc(sessionRef, {
            progress: "STARTED",
            activeScan: null,
          });
        }
      } catch (error) {
        // This one-shot getDoc/updateDoc used to be unguarded and, on failure,
        // blocked sharing entirely (see the up-front setClient fix above). Log
        // to pwa_logs with the Firestore `code` so we can actually see the
        // failure rate + cause (permission-denied = auth-token race,
        // unavailable = network) instead of it dying silently in the console.
        logError("[AvatarSelect] widget_session read/update failed", {
          code: (error as { code?: string })?.code ?? null,
          message: (error as Error)?.message ?? null,
          sessionId,
        });
      }
    }
    handleGettingSession();
    // navigateTo/validOrg/clientName are intentionally excluded: the effect
    // should run only when the deep link (location.search) or auth changes, not
    // on every render (navigateTo is a new ref each render) or when clientName
    // updates (it is set inside).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user]);

  const handleSelectExistingAvatar = async () => {
    if (!selectedScanId) return;
    const scanRef = doc(db, "scans", selectedScanId);
    const sessionRef = session ? doc(db, "widget_sessions", session) : null;
    // don't allow sending Failed avatars
    const scanData = await getDoc(scanRef);
    if (scanData.exists()) {
      const { pipelineStatus = "FAILURE" } = scanData.data();
      if (pipelineStatus === "FAILURE") {
        return;
      }
    }
    try {
      try {
        // Update clientIds in the scan document
        await updateDoc(scanRef, {
          clientIds: arrayUnion(client),
        });
      } catch (error) {
        console.error("Error updating Firestore scan:", error);
        // this means the scan is still submitting in this case we need to update the session doc only
        try {
          if (session && sessionRef) {
            await updateDoc(sessionRef, {
              activeScan: null,
              progress: "SUBMITTING",
            });
            navigateTo("/measurements-sent", {
              state: {
                orgName: clientName,
              },
            });
            return;
          }
        } catch (error) {
          console.error("Error updating Firestore session:", error);
          alert("There was an error updating the avatar. Please try again.");
        }
      }
      // If sessionId exists, update the widget_sessions document
      if (session && sessionRef) {
        await updateDoc(sessionRef, {
          activeScan: {
            id: selectedScanId,
            name: name,
            timestamp: Math.floor(Date.now() / 1000),
            userId: user?.uid,
          },
          progress: "UPLOADED",
        });
      }
      navigateTo("/measurements-sent", {
        state: {
          orgName: clientName,
        },
      });
    } catch (error) {
      console.error("Error updating Firestore session:", error);
      alert("There was an error updating the avatar. Please try again.");
    }
  };

  const createNewAvatar = async () => {
    const sessionRef = session ? doc(db, "widget_sessions", session) : null;
    if (client) {
      setDeeplinkFlag();
      setAvatarSharingData({
        clientId: client,
        sessionId: session || null,
        orgName: clientName || null,
        shareCode: null,
      });
    }
    if (session && sessionRef) {
      await updateDoc(sessionRef, {
        activeScan: null,
        progress: "STARTED",
      });
    }

    // Same deliberate "start another scan" press as the avatars list's New
    // Avatar button — the user knows the previous scan was submitted, so the
    // form does not need to tell them, and `isSharableScan` keeps a
    // just-submitted scan ("Avatar processing") on the list they pressed from.
    // Unscoped and bounded for the same reasons as that button. See
    // `acknowledgeScanSubmitted`.
    acknowledgeScanSubmitted();
    // Navigate without state - NewAvatarScreen will determine deepLink from storage
    navigateTo("/new-avatar-screen");
  };

  // Who the banner and the cancel dialog name. `client` is set synchronously
  // from the deep link, so this is "Selected Org" from the first paint and
  // becomes the real name the moment `validOrg` answers — rather than the
  // blank it used to show for as long as that took, or forever when the
  // lookup found nothing. Display only: the recipient is `client` either way.
  //
  // "" (no clientId at all) hides the banner. /send-avatar reached without a
  // clientId used to render "Sending Avatar to:" followed by nothing, which
  // told the user about a share that was not happening.
  const recipientLabel = shareRecipientLabel({
    orgName: clientName,
    clientId: client,
  });

  return (
    <>
      {loading || showSpinner ? (
        <div className="loader-container">
          <CircularProgress />
        </div>
      ) : (
        <div
          style={{
            background: "var(--sm-bg, rgba(240, 242, 250, 1))",
          }}
          className="main-grid avatar-select"
        >
          <CancelModal
            clientName={recipientLabel}
            open={isModalOpen}
            onClose={handleClose}
            onCancel={handleCancel}
          />

          <div className="list-avatar-header-wrap">
            <div className="list-avatar-header-container">
              <div className="avatars">
                <h1>Select an avatar</h1>
              </div>
              <div
                onClick={() => {
                  handleOpen("/settings");
                }}
                className="settings"
              >
                <Settings className="settings-icon" />
              </div>
            </div>
            {recipientLabel ? (
              <div className="deeplink">
                <p className="p2">
                  Sending Avatar to: <b>{recipientLabel}</b>
                </p>
              </div>
            ) : null}
          </div>
          <div className="list-avatar-main-wrap">
            {docs === undefined ? (
              // No answer yet (the first load passed its 10 s deadline, or
              // failed). The screen is usable; the list is not known.
              <div className="list-avatar-pending">
                <CircularProgress />
              </div>
            ) : scans.length === 0 ? (
              <div className="no-scan-data">
                <div className="avatar-icon">
                  <svg
                    width="115"
                    height="160"
                    viewBox="0 0 115 160"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 124.618C20 111.409 24.3684 106.11 20 97.5035C14.4223 86.5146 0 90.3069 0 79.9999C0 61.9072 24.5 55.7521 24.5 35.6557C24.5 19.6351 20 -18.0002 69.5 11.4998C79.5173 17.4697 101.333 14.2278 104.519 19.6351C109.874 28.7232 115.218 30.9625 111.871 42.6557C108.933 52.9199 85 56.205 85 66.4999C85 76.2186 96.7551 85.2995 104.519 91.4999C117.828 102.128 120 124.618 97.8639 130.18C83.1442 133.879 67.1412 119.053 59 130.18C29.5 170.5 21.5 134 20 124.618Z"
                      fill="#E2E8F5"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M63.1823 43.6899C63.1823 45.8469 62.3628 48.6643 60.9865 50.9197C59.5339 53.3001 58.0002 54.2404 56.936 54.2404C55.8718 54.2404 54.3381 53.3001 52.8855 50.9197C51.5092 48.6643 50.6897 45.8469 50.6897 43.6899C50.6897 39.4719 53.7211 36.5 56.936 36.5C60.1509 36.5 63.1823 39.4719 63.1823 43.6899ZM56.936 56.7404C61.7664 56.7404 65.6823 49.0414 65.6823 43.6899C65.6823 38.3383 61.7664 34 56.936 34C52.1055 34 48.1897 38.3383 48.1897 43.6899C48.1897 49.0414 52.1055 56.7404 56.936 56.7404ZM111.333 64.5942C111.333 63.9039 110.774 63.3442 110.083 63.3442H3.25C2.55964 63.3442 2 63.9039 2 64.5942C2 65.2846 2.55964 65.8442 3.25 65.8442H110.083C110.774 65.8442 111.333 65.2846 111.333 64.5942ZM33.1443 155.318C35.2441 140.71 37.9846 121.51 40.2001 105.962C41.3079 98.1873 42.2844 91.3261 42.984 86.4083L43.8075 80.6183L44.0293 79.058L44.0867 78.6538L44.1013 78.551L44.105 78.5251L44.1059 78.5186L44.1062 78.517C44.1062 78.5166 44.1063 78.5164 42.8687 78.3405C41.6311 78.1647 41.6311 78.1649 41.631 78.1652L41.6308 78.1668L41.6299 78.1734L41.6262 78.1993L41.6116 78.3021L41.5542 78.7062L41.3324 80.2664L40.5089 86.0562C39.8094 90.9738 38.8329 97.8349 37.7251 105.609C35.5096 121.157 32.7693 140.356 30.6698 154.963C30.3571 157.138 31.6608 158.727 33.3316 159.142C34.9945 159.556 36.8962 158.776 37.6771 156.735L53.4372 115.536C53.9854 114.103 55.3607 113.157 56.895 113.157C58.4259 113.157 59.799 114.099 60.3495 115.528L75.8787 155.822C76.6739 157.885 78.6111 158.638 80.279 158.21C81.9525 157.781 83.2801 156.181 82.9484 153.986C80.7519 139.451 78.0067 120.497 75.8103 105.174C74.7122 97.5136 73.7514 90.7614 73.0651 85.9244C72.7219 83.5059 72.4474 81.5663 72.2587 80.2313L72.0421 78.698L71.9861 78.301L71.9719 78.2L71.9683 78.1746L71.9674 78.1682L71.9672 78.1666C71.9671 78.1663 71.9671 78.1661 70.7293 78.3405C69.4916 78.515 69.4916 78.5152 69.4916 78.5155L69.4919 78.5171L69.4928 78.5235L69.4964 78.549L69.5106 78.6501L69.5667 79.0474L69.7833 80.5812C69.9721 81.9166 70.2467 83.8566 70.5899 86.2756C71.2763 91.1135 72.2373 97.8669 73.3356 105.529C75.5321 120.853 78.2785 139.815 80.4764 154.36C80.6063 155.219 80.1558 155.661 79.658 155.788C79.1546 155.918 78.5274 155.743 78.2115 154.923L62.6823 114.629C61.7599 112.235 59.4597 110.657 56.895 110.657C54.3246 110.657 52.0206 112.242 51.1022 114.643L35.3421 155.841C35.0259 156.668 34.4079 156.834 33.9346 156.716C33.4691 156.601 33.0201 156.183 33.1443 155.318Z"
                      fill="black"
                    />
                  </svg>
                </div>
                <div className="no-recent-avatars">No recent avatars</div>
                <div className="create">Create a new one below</div>
              </div>
            ) : (
              <div className="recent-scan-data">
                {scans.map((scan: any, index: number) => (
                  <label htmlFor={scan?.id} key={index} className="scan-item">
                    <div className="scan-details">
                      <div className="title">
                        <div className="name">
                          <PermIdentityIcon />
                          {scan.name}{" "}
                        </div>
                        <div className="arrow-icon">
                          <input
                            id={scan?.id}
                            name={scan?.id}
                            onChange={(ev) => {
                              if (ev.target.checked) {
                                userPicked.current = true;
                                setName(scan.name);
                                setSelectedScanId(scan.id);
                              }
                            }}
                            checked={selectedScanId === scan.id}
                            className="input"
                            type="radio"
                            disabled={!isSharableScan(scan)}
                          />
                          <span className="input"></span>
                        </div>
                      </div>

                      <div className="date">{scan.date}</div>

                      <div className="avatar-status">
                        <span
                          className="circle"
                          style={{ backgroundColor: scan.color }}
                        ></span>
                        {scan.status}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="list-avatar-footer-container">
            <div className="select">
              <button
                onClick={() => handleOpen("/")}
                className="secondary-button"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleSelectExistingAvatar();
                }}
                className="new-avatar-button"
                disabled={scans.length === 0 || !client}
              >
                Confirm
              </button>{" "}
            </div>
            <button
              onClick={() => {
                createNewAvatar();
              }}
              className="create-avatar-btn p1"
              disabled={!canCreateNewScan}
            >
              + Create new avatar
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default AvatarSelect;
